import { Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import { FilterFormWrapper } from '@/components/FilterFormWrapper'
import { Select } from '@/components/ui/select'
import { fetchUnits } from '@/api/units.api'
import { getMailTemplateFunctions } from '@/api/mail-templates.api'

export interface MailTemplateFilterValues {
    name: string
    title: string
    functionId?: number
    unitId?: string
    unitName?: string
    time?: DateRangeValue
    status?: number
}

interface MailTemplateFilterPanelProps {
    values: MailTemplateFilterValues
    onChange: (values: MailTemplateFilterValues) => void
    onSearch: () => void
    showUnitColumn?: boolean
}

export function MailTemplateFilterPanel({ values, onChange, onSearch, showUnitColumn = true }: MailTemplateFilterPanelProps) {
    function set<TKey extends keyof MailTemplateFilterValues>(key: TKey, value: MailTemplateFilterValues[TKey]) {
        onChange({ ...values, [key]: value })
    }

    const { data: functionsData } = useQuery({
        queryKey: ['mail-template-functions'],
        queryFn: getMailTemplateFunctions
    })

    const functionOptions = functionsData?.map(f => ({ value: f.value, label: f.label })) || []

    return (
        <FilterFormWrapper onSearch={onSearch} className="rounded-xl border border-gray-300 bg-white px-6 py-5">
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-end gap-4">

                    <div className="flex min-w-[200px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Tên mẫu</span>
                        <Input
                            placeholder="Nhập từ khóa"
                            value={values.name}
                            onChange={(e: { target: { value: string } }) => set('name', e.target.value)}
                        />
                    </div>

                    <div className="flex min-w-[200px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Tiêu đề</span>
                        <Input
                            placeholder="Nhập từ khóa"
                            value={values.title}
                            onChange={(e: { target: { value: string } }) => set('title', e.target.value)}
                        />
                    </div>

                    <div className="flex min-w-[150px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Chức năng</span>
                        <Select
                            placeholder="Tất cả"
                            value={values.functionId}
                            className="w-full"
                            onValueChange={(v) => set('functionId', v)}
                            allowClear
                            options={functionOptions}
                        />
                    </div>

                    {showUnitColumn && (
                        <div className="flex min-w-[150px] flex-1 flex-col gap-1">
                            <span className="text-sm font-medium">Đơn vị</span>
                            <Select
                                initialPage={0}
                                cacheKey="mail-template-filter-unit-select"
                                lazy
                                placeholder="Tất cả"
                                value={values.unitId || undefined}
                                className="w-full"
                                allowClear
                                enableRemoteSearch
                                allLabel="Tất cả"
                                initialOptions={
                                    values.unitId && values.unitName
                                        ? [{ value: values.unitId, label: values.unitName }]
                                        : []
                                }
                                loadOptions={async ({ page, signal, searchText }) => {
                                    const res = await fetchUnits(
                                        page,
                                        10,
                                        { keySearch: searchText?.trim() || undefined },
                                        signal
                                    )
                                    const arr = Array.isArray(res.items) ? res.items : []
                                    const hasNextPage = arr.length >= 10
                                    return {
                                        options: arr.map((u: any) => ({ value: u.id, label: u.name })),
                                        hasNextPage,
                                        nextPage: hasNextPage ? page + 1 : undefined,
                                    }
                                }}
                                onLazyValueChange={(unitId, option) => {
                                    onChange({
                                        ...values,
                                        unitId: unitId,
                                        unitName: option?.label ? String(option.label) : undefined,
                                    })
                                }}
                            />
                        </div>
                    )}

                    <div className="flex min-w-[150px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Trạng thái</span>
                        <Select
                            placeholder="Tất cả"
                            value={values.status}
                            className="w-full"
                            onValueChange={(v) => set('status', v)}
                            allowClear
                            options={[
                                { value: 1, label: 'Hoạt động' },
                                { value: 0, label: 'Không hoạt động' }
                            ]}
                        />
                    </div>

                    <div className="flex min-w-[240px] flex-1 flex-col gap-1">
                        <span className="text-sm font-medium">Thời gian</span>
                        <DateRangePicker
                            value={values.time}
                            onChange={(v) => set('time', v)}
                            className="w-full rounded-full"
                        />
                    </div>

                    <Button
                        variant="outline"
                        size="md"
                        className="gap-2 rounded-full border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10 h-10 px-6"
                        onClick={onSearch}
                    >
                        <Search size={16} /> Tìm kiếm
                    </Button>
                </div>
            </div>
        </FilterFormWrapper>
    )
}